import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Shield, TrendingUp, Heart, Laptop, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { DotCluster } from '@/components/brand/EchoArc'
import { DEMO_SEGMENTS } from '@/lib/demo/data'
import { useSession } from '@/lib/session/SessionContext'
import { DEMO_CONFIGS, DEMO_IDS, DEMO_AHA_CARDS, type DemoId } from '@/lib/demo/constants'
import { loadDemo, getPreComputedPreRead } from '@/lib/demo/DemoLoader'

const DEMO_PROJECT_ID = 'demo'

const DEMO_TABS: Array<{ demoId: DemoId; label: string; Icon: React.ComponentType<{ className?: string }> }> = [
  { demoId: 'insurance-renewal', label: 'Insurance', Icon: Shield },
  { demoId: 'financial-services-fees', label: 'Financial Services', Icon: TrendingUp },
  { demoId: 'healthcare-reminder', label: 'Healthcare', Icon: Heart },
  { demoId: 'saas-churn', label: 'SaaS', Icon: Laptop },
]

const SEVERITY_STYLES: Record<string, { bg: string; border: string; badge: 'danger' | 'warning' | 'success' }> = {
  high: { bg: 'bg-red-50', border: 'border-red-200', badge: 'danger' },
  medium: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'warning' },
  low: { bg: 'bg-green-50', border: 'border-green-200', badge: 'success' },
}

export function DemoWorkspace() {
  const { session } = useSession()
  const [liveHref, setLiveHref] = useState('/app/onboarding')
  const [searchParams, setSearchParams] = useSearchParams()

  // Resolve active demo from ?industry= URL param
  const industryParam = searchParams.get('industry') as DemoId | null
  const activeDemoId: DemoId =
    industryParam && (DEMO_IDS as readonly string[]).includes(industryParam)
      ? industryParam
      : 'insurance-renewal'

  const demoData = loadDemo(activeDemoId)
  const preRead = getPreComputedPreRead(activeDemoId)
  const ahaCards = DEMO_AHA_CARDS[activeDemoId]

  // For logged-in users, resolve their first real project link
  useEffect(() => {
    if (!session) return
    const token = localStorage.getItem('echo_session')
    if (!token) return
    fetch('/api/projects', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() as Promise<Array<{ project_id: string }>> : [])
      .then(projects => {
        if (projects[0]) setLiveHref(`/app/projects/${projects[0].project_id}/echo-panel`)
      })
      .catch(() => {})
  }, [session])

  return (
    <div className="p-6 space-y-8 max-w-4xl pb-20 md:pb-6">
      {/* Live mode banner for authenticated users */}
      {session && (
        <div className="flex items-center justify-between gap-4 px-4 py-3 bg-echo-indigo/5 border border-echo-indigo/20 rounded-xl">
          <div className="flex items-center gap-2">
            <Badge variant="demo" size="sm">Demo Preview</Badge>
            <span className="text-xs text-slate-600">You're exploring simulated data — your real workspace has live data.</span>
          </div>
          <Link to={liveHref} className="shrink-0">
            <Button variant="primary" size="sm">← Your workspace</Button>
          </Link>
        </div>
      )}

      {/* Demo industry tab switcher */}
      <div
        role="tablist"
        aria-label="Demo industry switcher"
        className="flex gap-1 p-1 bg-soft-panel rounded-xl border border-border-gray overflow-x-auto"
      >
        {DEMO_TABS.map(({ demoId, label, Icon }) => {
          const isActive = activeDemoId === demoId
          return (
            <button
              key={demoId}
              role="tab"
              data-testid="demo-switcher-tab"
              data-industry={demoId}
              aria-selected={isActive}
              onClick={() => setSearchParams({ industry: demoId })}
              className={[
                'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors',
                isActive
                  ? 'bg-white text-echo-indigo shadow-sm border border-echo-indigo/20'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/60',
              ].join(' ')}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              {label}
            </button>
          )
        })}
      </div>

      {/* Project header */}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>
              {demoData.title}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Acme Research · Demo workspace · <Badge variant="demo" size="sm">Simulated demo metric</Badge>
            </p>
          </div>
          <Link to={`/app/projects/${DEMO_PROJECT_ID}/pre-read`}>
            <Button variant="primary">Run Pre-Read →</Button>
          </Link>
        </div>
      </div>

      {/* Approved headline banner */}
      <div className="flex items-start gap-3 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-xl">
        <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-indigo-100 border border-indigo-300 flex items-center justify-center">
          <span className="text-indigo-700 text-[10px] font-bold leading-none">A</span>
        </div>
        <div className="space-y-0.5">
          <p className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Approved Headline</p>
          <p className="text-sm font-medium text-indigo-900">{preRead.headline}</p>
        </div>
      </div>

      {/* Aha cards */}
      <div>
        <h2 className="text-base font-medium text-deep-ink mb-4">Key findings</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {ahaCards.map(card => {
            const styles = SEVERITY_STYLES[card.severity] ?? SEVERITY_STYLES.low
            return (
              <div
                key={card.card_id}
                data-testid={`aha-card-${card.type}`}
                className={`rounded-xl border p-4 space-y-2 ${styles.bg} ${styles.border}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-deep-ink leading-snug">{card.title}</p>
                  <div className="flex items-center gap-1 shrink-0">
                    {card.severity === 'high' && (
                      <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                    )}
                    <Badge variant={styles.badge} size="sm">{card.severity}</Badge>
                  </div>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">{card.body}</p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Evidence summary */}
      <Card className="bg-off-white border-0">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Survey responses', value: '1,248' },
            { label: 'Open-ended answers', value: '362' },
            { label: 'Interview snippets', value: '18' },
            { label: 'Prior concept tests', value: '4' },
          ].map(stat => (
            <div key={stat.label}>
              <p className="text-2xl font-medium text-deep-ink">{stat.value}</p>
              <p className="text-xs text-slate-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Echo readiness */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-deep-ink">Echo Readiness</h3>
            <Badge variant="success">High</Badge>
          </div>
          <div className="space-y-2">
            {[
              { label: 'Response volume', value: 'High', color: 'bg-validation-green' },
              { label: 'Open-ended depth', value: 'Medium', color: 'bg-uncertainty-amber' },
              { label: 'Segment coverage', value: 'High', color: 'bg-validation-green' },
              { label: 'Recency', value: 'High', color: 'bg-validation-green' },
              { label: 'Source diversity', value: 'High', color: 'bg-validation-green' },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-32">{item.label}</span>
                <div className="flex-1 h-1.5 bg-soft-panel rounded-full">
                  <div className={`h-full rounded-full ${item.color}`} style={{ width: item.value === 'High' ? '85%' : '55%' }} />
                </div>
                <Badge variant={item.value === 'High' ? 'success' : 'warning'} size="sm">{item.value}</Badge>
              </div>
            ))}
          </div>
          <div className="pt-2 border-t border-border-gray">
            <p className="text-xs text-slate-500">Available modes: Study Memory · Segment Echo · Validated Echo Panel</p>
          </div>
        </Card>

        <Card className="space-y-3">
          <h3 className="text-sm font-medium text-deep-ink">Research question</h3>
          <p className="text-sm font-medium text-deep-ink bg-soft-panel rounded-lg px-3 py-2">
            "{demoData.preReadQuestion}"
          </p>
          <div className="space-y-1">
            <p className="text-xs text-slate-400">Stimuli</p>
            {demoData.stimuli.slice(0, 3).map(s => (
              <div key={s.stimulus_id} className="text-xs text-slate-600 flex gap-1.5">
                <span className="text-human-teal font-medium shrink-0">{s.label}</span>
                <span className="truncate">{s.content}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Segments */}
      <div>
        <h2 className="text-base font-medium text-deep-ink mb-4">Echo Panel segments</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {(demoData.segments.length > 0 ? demoData.segments : DEMO_SEGMENTS).map(seg => (
            <Card key={seg.segment_id} className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <DotCluster count={Math.min(seg.respondent_count / 60, 20)} size={36} />
                <Badge variant={seg.evidence_strength === 'High' ? 'success' : seg.evidence_strength === 'Medium' ? 'warning' : 'danger'} size="sm">
                  {seg.evidence_strength}
                </Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-deep-ink">{seg.name}</p>
                <p className="text-xs text-slate-400">{seg.respondent_count.toLocaleString()} respondents</p>
              </div>
              {seg.dominant_themes && (
                <div className="space-y-1">
                  {seg.dominant_themes.slice(0, 2).map((theme: string) => (
                    <p key={theme} className="text-xs text-slate-500 flex items-start gap-1">
                      <span className="text-human-teal shrink-0">·</span>{theme}
                    </p>
                  ))}
                </div>
              )}
              {seg.representative_quotes && seg.representative_quotes[0] && (
                <blockquote className="text-xs italic text-slate-500 border-l-2 border-human-teal/30 pl-2">
                  "{seg.representative_quotes[0].text.slice(0, 100)}…"
                </blockquote>
              )}
            </Card>
          ))}
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Echo Panel', href: `/app/projects/${DEMO_PROJECT_ID}/echo-panel`, desc: 'View segment evidence' },
          { label: 'Run Pre-Read', href: `/app/projects/${DEMO_PROJECT_ID}/pre-read`, desc: 'Ensemble prediction' },
          { label: 'Validation', href: `/app/projects/${DEMO_PROJECT_ID}/validation`, desc: 'Simulate human results' },
          { label: 'Report', href: `/app/projects/${DEMO_PROJECT_ID}/report`, desc: 'Client-ready export' },
        ].map(action => (
          <Link key={action.href} to={action.href}>
            <Card className="hover:border-human-teal/50 transition-colors cursor-pointer h-full">
              <p className="text-sm font-medium text-deep-ink">{action.label}</p>
              <p className="text-xs text-slate-400 mt-1">{action.desc}</p>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  )
}
